// ===========================================
// navConfig — Configuration partagée des mégamenus
// Source unique pour la topnav desktop (navbar) et le drawer mobile (MobileNav).
// Reçoit la fonction de traduction `t` en argument.
// ===========================================
import React from 'react';
import {
    HomeOutlined,
    AppstoreOutlined,
    BarChartOutlined,
    SettingOutlined,
    TeamOutlined,
    UserOutlined,
    FileProtectOutlined,
    DollarOutlined,
    ExclamationCircleOutlined,
    FieldTimeOutlined,
    WarningOutlined,
    BankOutlined,
    SafetyCertificateOutlined,
    ShopOutlined,
    CarOutlined,
    DatabaseOutlined,
    AuditOutlined,
    ProfileOutlined,
} from '@ant-design/icons';

export const getNavConfig = (t) => {
    // ─── Dashboard (lien simple) ──────────────────────────────────────
    const dashboard = {
        key: 'dashboard',
        label: t('nav.dashboard'),
        icon: <HomeOutlined />,
        path: '/dashboard',
        exact: true,
        perm: 'dashboard:view',
    };

    // ─── Gestion ──────────────────────────────────────────────────────
    const gestion = {
        key: 'gestion',
        label: t('nav.gestion'),
        icon: <AppstoreOutlined />,
        // Route principale du module (clic sur l'entrée topbar => navigate ici)
        entry: '/dashboard/clients',
        matches: [
            // Liste + créa + détail (singulier vs pluriel)
            '/dashboard/clients', '/dashboard/client/', '/dashboard/nouveau-client',
            '/dashboard/contrats', '/dashboard/contrat/', '/dashboard/nouveau-contrat',
            '/dashboard/reglements', '/dashboard/nouveau-reglement', '/dashboard/detail-reglement', '/dashboard/etat-reglements',
            '/dashboard/echeances',
            '/dashboard/impayes',
            '/dashboard/sinistres', '/dashboard/sinistre/', '/dashboard/nouveau-sinistre',
        ],
        sections: [
            {
                title: t('nav.megamenu.section.gestion'),
                cols: 3,
                items: [
                    {
                        key: 'clients',
                        icon: <TeamOutlined />,
                        label: t('nav.clients'),
                        description: t('nav.megamenu.desc.clients'),
                        path: '/dashboard/clients',
                        // Active sur la liste, le détail (singulier) et la création
                        matches: ['/dashboard/clients', '/dashboard/client/', '/dashboard/nouveau-client'],
                        perm: 'clients:read',
                        primaryAction: {
                            label: t('nav.create_client'),
                            path: '/dashboard/nouveau-client',
                            perm: 'clients:create',
                        },
                    },
                    {
                        key: 'contrats',
                        icon: <FileProtectOutlined />,
                        label: t('nav.contrats'),
                        description: t('nav.megamenu.desc.contrats'),
                        path: '/dashboard/contrats',
                        matches: ['/dashboard/contrats', '/dashboard/contrat/', '/dashboard/nouveau-contrat'],
                        perm: 'contrats:read',
                        primaryAction: {
                            label: t('nav.create_contrat'),
                            path: '/dashboard/nouveau-contrat',
                            perm: 'contrats:create',
                        },
                    },
                    {
                        key: 'echeances',
                        icon: <FieldTimeOutlined />,
                        label: t('nav.echeances'),
                        description: t('nav.megamenu.desc.echeances'),
                        path: '/dashboard/echeances',
                        perm: 'contrats:read',
                    },
                    {
                        key: 'reglements',
                        icon: <DollarOutlined />,
                        label: t('nav.reglements'),
                        description: t('nav.megamenu.desc.reglements'),
                        path: '/dashboard/reglements',
                        matches: ['/dashboard/reglements', '/dashboard/nouveau-reglement', '/dashboard/detail-reglement'],
                        perm: 'reglements:read',
                    },
                    {
                        key: 'impayes',
                        icon: <ExclamationCircleOutlined />,
                        label: t('nav.impayes'),
                        description: t('nav.megamenu.desc.impayes'),
                        path: '/dashboard/impayes',
                        perm: 'impayes:read',
                    },
                    {
                        key: 'sinistres',
                        icon: <WarningOutlined />,
                        label: t('nav.sinistres'),
                        description: t('nav.megamenu.desc.sinistres'),
                        path: '/dashboard/sinistres',
                        matches: ['/dashboard/sinistres', '/dashboard/sinistre/', '/dashboard/nouveau-sinistre'],
                        perm: 'sinistres:read',
                        primaryAction: {
                            label: t('nav.create_sinistre'),
                            path: '/dashboard/nouveau-sinistre',
                            perm: 'sinistres:create',
                        },
                    },
                    {
                        key: 'etat-reglements',
                        icon: <ProfileOutlined />,
                        label: t('nav.megamenu.label.etat_reglements'),
                        description: t('nav.megamenu.desc.etat_reglements'),
                        path: '/dashboard/etat-reglements',
                        perm: 'reglements:read',
                    },
                ],
            },
            {
                title: t('nav.megamenu.section.actions'),
                type: 'actions',
                items: [
                    {
                        key: 'new-client',
                        label: t('nav.create_client'),
                        path: '/dashboard/nouveau-client',
                        perm: 'clients:create',
                    },
                    {
                        key: 'new-contrat',
                        label: t('nav.create_contrat'),
                        path: '/dashboard/nouveau-contrat',
                        perm: 'contrats:create',
                    },
                    {
                        key: 'new-sinistre',
                        label: t('nav.create_sinistre'),
                        path: '/dashboard/nouveau-sinistre',
                        perm: 'sinistres:create',
                    },
                ],
            },
        ],
    };

    // ─── Rapports ─────────────────────────────────────────────────────
    const rapports = {
        key: 'rapports',
        label: t('nav.rapports'),
        icon: <BarChartOutlined />,
        entry: '/dashboard/bilans',
        matches: [
            '/dashboard/bilans',
            '/dashboard/bordereau-production',
            '/dashboard/etat-clients',
            '/dashboard/etat-client/',
        ],
        sections: [
            {
                title: t('nav.megamenu.section.bilans'),
                cols: 3,
                items: [
                    {
                        key: 'bilans',
                        icon: <BarChartOutlined />,
                        label: t('nav.bilans'),
                        description: t('nav.megamenu.desc.bilans'),
                        path: '/dashboard/bilans',
                        perm: 'bilans:read',
                    },
                    {
                        key: 'bordereau-production',
                        icon: <ProfileOutlined />,
                        label: t('nav.megamenu.label.bordereau'),
                        description: t('nav.megamenu.desc.bordereau'),
                        path: '/dashboard/bordereau-production',
                        perm: 'bilans:read',
                    },
                    {
                        key: 'etat-clients',
                        icon: <TeamOutlined />,
                        label: t('nav.megamenu.label.etat_clients'),
                        description: t('nav.megamenu.desc.etat_clients'),
                        path: '/dashboard/etat-clients',
                        matches: ['/dashboard/etat-clients', '/dashboard/etat-client/'],
                        perm: 'bilans:read',
                    },
                ],
            },
        ],
    };

    // ─── Paramétrage ──────────────────────────────────────────────────
    const parametrage = {
        key: 'parametrage',
        label: t('nav.config'),
        icon: <SettingOutlined />,
        entry: '/dashboard/compagnies',
        matches: [
            '/dashboard/compagnies',
            '/dashboard/types-assurance',
            '/dashboard/agences',
            '/dashboard/marques-vehicule',
            '/dashboard/referentiels-vehicule',
            '/dashboard/parametrages',
        ],
        sections: [
            {
                title: t('nav.megamenu.section.referentiels'),
                cols: 3,
                items: [
                    {
                        key: 'compagnies',
                        icon: <BankOutlined />,
                        label: t('nav.compagnies'),
                        description: t('nav.megamenu.desc.compagnies'),
                        path: '/dashboard/compagnies',
                        perm: 'compagnies:read',
                    },
                    {
                        key: 'types-assurance',
                        icon: <SafetyCertificateOutlined />,
                        label: t('nav.types_assurance'),
                        description: t('nav.megamenu.desc.types_assurance'),
                        path: '/dashboard/types-assurance',
                        perm: 'typesAssurance:read',
                    },
                    {
                        key: 'agences',
                        icon: <ShopOutlined />,
                        label: t('nav.agences'),
                        description: t('nav.megamenu.desc.agences'),
                        path: '/dashboard/agences',
                        perm: 'agences:read',
                    },
                    {
                        key: 'marques-vehicule',
                        icon: <CarOutlined />,
                        label: t('nav.megamenu.label.marques_vehicule'),
                        description: t('nav.megamenu.desc.marques_vehicule'),
                        path: '/dashboard/marques-vehicule',
                        perm: 'parametrages:read',
                    },
                    {
                        key: 'referentiels-vehicule',
                        icon: <DatabaseOutlined />,
                        label: t('nav.megamenu.label.referentiels_vehicule'),
                        description: t('nav.megamenu.desc.referentiels_vehicule'),
                        path: '/dashboard/referentiels-vehicule',
                        perm: 'parametrages:read',
                    },
                    {
                        key: 'parametrages',
                        icon: <SettingOutlined />,
                        label: t('nav.parametrages'),
                        description: t('nav.megamenu.desc.parametrages'),
                        path: '/dashboard/parametrages',
                        perm: 'parametrages:read',
                    },
                ],
            },
        ],
    };

    // ─── Administration ───────────────────────────────────────────────
    const admin = {
        key: 'admin',
        label: t('nav.admin'),
        icon: <TeamOutlined />,
        entry: '/dashboard/gestion-admins',
        matches: [
            '/dashboard/gestion-admins',
            '/dashboard/ajouter-admin',
            '/dashboard/roles',
            '/dashboard/ajouter-role',
            '/dashboard/modifier-role',
            '/dashboard/audit-log',
        ],
        sections: [
            {
                title: t('nav.megamenu.section.administration'),
                cols: 3,
                items: [
                    {
                        key: 'admin-list',
                        icon: <UserOutlined />,
                        label: t('nav.users'),
                        description: t('nav.megamenu.desc.users'),
                        path: '/dashboard/gestion-admins',
                        matches: ['/dashboard/gestion-admins', '/dashboard/ajouter-admin'],
                        perm: 'users:read',
                        primaryAction: {
                            label: t('nav.megamenu.label.new_admin'),
                            path: '/dashboard/ajouter-admin',
                            perm: 'users:create',
                        },
                    },
                    {
                        key: 'roles',
                        icon: <SafetyCertificateOutlined />,
                        label: t('nav.roles'),
                        description: t('nav.megamenu.desc.roles'),
                        path: '/dashboard/roles',
                        matches: ['/dashboard/roles', '/dashboard/ajouter-role', '/dashboard/modifier-role'],
                        perm: 'roles:read',
                    },
                    {
                        key: 'audit',
                        icon: <AuditOutlined />,
                        label: t('nav.audit'),
                        description: t('nav.megamenu.desc.audit'),
                        path: '/dashboard/audit-log',
                        matches: ['/dashboard/audit-log'],
                        perm: 'audit:read',
                    },
                ],
            },
            {
                title: t('nav.megamenu.section.actions'),
                type: 'actions',
                items: [
                    {
                        key: 'new-admin',
                        label: t('nav.megamenu.label.new_admin'),
                        path: '/dashboard/ajouter-admin',
                        perm: 'users:create',
                    },
                ],
            },
        ],
    };

    return {
        dashboard,
        megamenus: [gestion, rapports, admin, parametrage],
    };
};
